import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@loomlite/db-types';

/**
 * The browser Supabase client, typed against the generated schema.
 *
 * All four apps read the same two environment variables and point at the same
 * project, and three of them had a near-identical copy of this file. Two of those
 * three — comp and social — created the client WITHOUT the `<Database>` generic,
 * so every query in them returned `any`: no column checking, no row-shape
 * checking, and no warning when a select named a column that does not exist.
 * Sharing one typed client is how that gets fixed rather than re-litigated per app.
 *
 * `persistSession` and `autoRefreshToken` are left at their defaults, which are
 * both `true`. comp set them explicitly; that was a no-op and is not carried over.
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
	throw new Error(
		'Missing required Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY',
	);
}

export const supabase: SupabaseClient<Database> = createClient<Database>(
	supabaseUrl,
	supabaseAnonKey,
);

/** The resolved project URL, for code that needs to build a function or storage URL. */
export const SUPABASE_URL: string = supabaseUrl;

/** The anon key, for callers that must pass it explicitly (e.g. a custom fetch). */
export const SUPABASE_ANON_KEY: string = supabaseAnonKey;

export type { Database, SupabaseClient };
